import React, { useMemo, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useData } from '../context/DataContext';
import { Topbar } from '../components/Topbar';
import { 
  Star, BookOpen, TrendingUp, CheckCircle2, 
  AlertCircle, Clock, FileText, ClipboardList,
  MessageCircle, XCircle
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, Badge, Button } from '../components/ui';
import { createPortal } from 'react-dom';

export function GradesPage() {
  const { user } = useAuth();
  const { grades, studentEnrollments, professors, assessments } = useData();
  const [chatProfessor, setChatProfessor] = useState(null);

  const isEnrolled = studentEnrollments && studentEnrollments.length > 0;
  const currentEnrollment = isEnrolled ? studentEnrollments[0] : null;

  // Filtrar notas do aluno logado
  const myGrades = useMemo(() => {
    if (!user) return [];
    return grades.filter(g => g.enrollmentId === user.enrollmentId);
  }, [grades, user]);

  // Agrupar notas por matéria
  const gradesBySubject = useMemo(() => {
    if (!currentEnrollment) return {};
    
    // Matérias que o aluno está matriculado
    const subjects = {};
    const courseName = currentEnrollment.course || currentEnrollment.courseName;
    
    // Pegar as matérias das notas já lançadas E a matéria atual
    const relevantSubjects = [...new Set([
      courseName,
      ...myGrades.map(g => g.subject)
    ])];

    relevantSubjects.forEach(sub => {
      const subGrades = myGrades.filter(g => g.subject === sub);
      const average = subGrades.length > 0 
        ? (subGrades.reduce((acc, curr) => acc + Number(curr.grade), 0) / subGrades.length).toFixed(1)
        : '0.0';

      // Tentar encontrar o professor desta matéria
      const professor = professors.find(p => 
        p.courses && p.courses.toLowerCase().includes(sub.toLowerCase())
      );

      subjects[sub] = {
        name: sub,
        grades: subGrades,
        average: average,
        professor: professor,
        status: Number(average) >= 7 ? 'Aprovado' : subGrades.length > 0 ? 'Em Recuperação' : 'Sem Notas'
      };
    });

    return subjects;
  }, [myGrades, currentEnrollment, professors]);

  if (!user) return null;

  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }} 
      animate={{ opacity: 1, y: 0 }}
      className="dashboardWrapper"
    >
      <Topbar />

      <header className="mb-8">
        <h2 className="text-4xl font-black mb-2">Minhas Notas 📊</h2>
        <p className="text-muted">Acompanhe seu desempenho acadêmico, trabalhos e avaliações.</p>
      </header>

      {!isEnrolled ? (
        <Card className="text-center py-20">
          <div className="w-20 h-20 bg-purple/10 rounded-full flex items-center justify-center mx-auto mb-6 text-purple">
            <AlertCircle size={40} />
          </div>
          <h3 className="text-2xl font-bold mb-2">Matrícula Não Localizada</h3>
          <p className="text-muted mb-8 max-w-md mx-auto">
            Você ainda não possui uma matrícula ativa em nenhum curso. Realize sua matrícula para visualizar suas notas.
          </p>
          <Button onClick={() => window.location.href = '/cursos'}>Ver Catálogo de Cursos</Button>
        </Card>
      ) : (
        <div className="space-y-8">
          {/* Resumo Geral */}
          <section className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <article className="stat purple">
              <div className="statIcon"><TrendingUp size={30}/></div>
              <div>
                <h3>Média Geral</h3>
                <strong>
                  {(Object.values(gradesBySubject).reduce((acc, curr) => acc + Number(curr.average), 0) / 
                    (Object.keys(gradesBySubject).length || 1)).toFixed(1)}
                </strong>
                <p>Desempenho total</p>
              </div>
            </article>
            <article className="stat green">
              <div className="statIcon"><CheckCircle2 size={30}/></div>
              <div>
                <h3>Matérias Aprovadas</h3>
                <strong>{Object.values(gradesBySubject).filter(s => Number(s.average) >= 7).length}</strong>
                <p>De {Object.keys(gradesBySubject).length} cursadas</p>
              </div>
            </article>
            <article className="stat orange">
              <div className="statIcon"><Clock size={30}/></div>
              <div>
                <h3>Pendências</h3>
                <strong>{Object.values(gradesBySubject).filter(s => Number(s.average) < 7 && s.grades.length > 0).length}</strong>
                <p>Avaliações abaixo da média</p>
              </div>
            </article>
          </section>

          {/* Detalhamento por Matéria */}
          <section className="space-y-6">
            <div className="flex items-center justify-between">
              <h3 className="text-2xl font-black flex items-center gap-3">
                <BookOpen className="text-purple" />
                Disciplinas em Andamento
              </h3>
              <div className="flex gap-2">
                {Object.values(gradesBySubject).some(s => s.professor) && (
                  <Button 
                    variant="purple" 
                    className="gap-2 px-6 py-3 rounded-2xl font-black uppercase tracking-widest text-xs shadow-xl shadow-purple/20"
                    onClick={() => {
                      const firstProf = Object.values(gradesBySubject).find(s => s.professor)?.professor;
                      if (firstProf) setChatProfessor(firstProf);
                    }}
                  >
                    <MessageCircle size={18} /> Contactar professor
                  </Button>
                )}
              </div>
            </div>

            {Object.values(gradesBySubject).map((subject, idx) => (
              <Card 
                key={idx} 
                title={subject.name}
                subtitle={`Média Atual: ${subject.average}`}
                actions={
                  <div className="flex items-center gap-3">
                    <Badge variant={Number(subject.average) >= 7 ? 'green' : 'orange'}>
                      {subject.status}
                    </Badge>
                  </div>
                }
              >
                <div className="mt-6 overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="text-left border-b border-white/5">
                        <th className="pb-4 font-black uppercase text-[10px] tracking-widest text-muted">Avaliação</th>
                        <th className="pb-4 font-black uppercase text-[10px] tracking-widest text-muted">Data</th>
                        <th className="pb-4 font-black uppercase text-[10px] tracking-widest text-muted">Tipo</th>
                        <th className="pb-4 font-black uppercase text-[10px] tracking-widest text-muted text-right">Nota</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-white/5">
                      {subject.grades.length > 0 ? subject.grades.map((grade, gIdx) => (
                        <tr key={gIdx} className="group hover:bg-white/[0.02] transition-all">
                          <td className="py-4">
                            <div className="flex items-center gap-3">
                              <div className="p-2 bg-purple/10 rounded-lg text-purple">
                                <ClipboardList size={16} />
                              </div>
                              <span className="font-bold">
                                {grade.title || assessments.find(a => a.id === grade.assessmentId)?.name || grade.assessmentId || 'Avaliação'}
                              </span>
                            </div>
                          </td>
                          <td className="py-4 text-sm text-muted">
                            {new Date(grade.date).toLocaleDateString('pt-BR')}
                          </td>
                          <td className="py-4">
                            <Badge variant="secondary" className="text-[10px]">Prova/Trabalho</Badge>
                          </td>
                          <td className="py-4 text-right">
                            <b className={`text-lg ${Number(grade.grade) >= 7 ? 'text-green' : 'text-red'}`}>
                              {Number(grade.grade).toFixed(1)}
                            </b>
                          </td>
                        </tr>
                      )) : (
                        <tr>
                          <td colSpan="4" className="py-10 text-center text-muted italic">
                            Nenhuma nota lançada para esta disciplina ainda.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Barra de Progresso da Média */}
                <div className="mt-8 pt-6 border-t border-white/5">
                  <div className="flex justify-between items-end mb-2">
                    <span className="text-xs font-black uppercase tracking-widest text-muted">Progresso para Aprovação</span>
                    <span className="text-xs font-bold">{Math.min((Number(subject.average) / 7) * 100, 100).toFixed(0)}%</span>
                  </div>
                  <div className="h-2 bg-white/5 rounded-full overflow-hidden">
                    <motion.div 
                      initial={{ width: 0 }}
                      animate={{ width: `${Math.min((Number(subject.average) / 7) * 100, 100)}%` }}
                      className={`h-full ${Number(subject.average) >= 7 ? 'bg-green shadow-[0_0_10px_rgba(34,197,94,0.3)]' : 'bg-orange shadow-[0_0_10px_rgba(249,115,22,0.3)]'}`}
                    />
                  </div>
                </div>
              </Card>
            ))}
          </section>

          {/* Dicas e Próximos Passos */}
          <section className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card title="Suporte Acadêmico">
              <p className="text-sm text-muted mb-4">
                Nosso chat interno permite que você fale diretamente com seus professores sobre dúvidas em avaliações e conteúdo.
              </p>
              <div className="flex items-center gap-3 p-4 bg-purple/5 border border-purple/10 rounded-2xl">
                <MessageCircle className="text-purple" size={24} />
                <div className="text-xs text-muted leading-relaxed">
                  Use o botão <strong>Contatar Professor</strong> acima em cada matéria para iniciar uma conversa.
                </div>
              </div>
            </Card>
            <Card title="Discord Oficial">
              <p className="text-sm text-muted mb-4">
                Fique atento ao cronograma de avaliações e avisos gerais postados no servidor da universidade.
              </p>
              <Button variant="outline" className="w-full" onClick={() => window.open('https://discord.gg/lumenfall', '_blank')}>
                Acessar Discord
              </Button>
            </Card>
          </section>
        </div>
      )}

      <AnimatePresence>
        {chatProfessor && (
          <ChatModal
            professor={chatProfessor}
            onClose={() => setChatProfessor(null)}
          />
        )}
      </AnimatePresence>
    </motion.div>
  );
}

function ChatModal({ professor, onClose }) {
  const { user } = useAuth();
  const { getConversationId, getConversationMessages, sendChatMessage } = useData();
  const [text, setText] = useState('');

  if (!professor) return null;

  const profName = professor.fullName || professor.name || 'Professor';
   const profId = professor.enrollmentId; // Sempre usar enrollmentId para professores
   const fromId = user.enrollmentId || user.citizenId; // Usar ID persistente do aluno
   const conversationId = getConversationId(fromId, profId);
   const messages = getConversationMessages(conversationId);

  const handleSend = () => {
    const clean = String(text || '').trim();
    if (!clean) return;
    sendChatMessage({ conversationId, fromId, toId: profId, text: clean });
    setText('');
  };

  return createPortal(
    <div className="fixed inset-0 flex items-center justify-center" style={{ zIndex: 2147483647, padding: 16 }}>
      <div
        onClick={onClose}
        style={{
          position: 'absolute',
          inset: 0,
          backgroundColor: 'rgba(3, 7, 18, 0.92)',
          backdropFilter: 'blur(18px)',
        }}
      />
      <motion.div
        initial={{ opacity: 0, scale: 0.92, y: 24 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.92, y: 24 }}
        style={{
          position: 'relative',
          width: 'min(900px, calc(100% - 32px))',
          height: 'min(640px, 85vh)',
          backgroundColor: '#0a1120',
          border: '1px solid rgba(255,255,255,0.10)',
          borderRadius: 24,
          overflow: 'hidden',
          boxShadow: '0 28px 90px rgba(0,0,0,0.55)',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div style={{ padding: 16, borderBottom: '1px solid rgba(255,255,255,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
            <div style={{
              width: 36,
              height: 36,
              borderRadius: 12,
              background: 'rgba(139,92,246,0.15)',
              border: '1px solid rgba(139,92,246,0.25)',
              color: 'var(--purple)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 900,
              flexShrink: 0
            }}>
              {(profName || 'P')[0]}
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 12, fontWeight: 900, color: '#fff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                Prof. {profName}
              </div>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.40)' }}>
                Chat acadêmico • {professor.courses}
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              width: 40,
              height: 40,
              borderRadius: 14,
              border: '1px solid rgba(255,255,255,0.10)',
              background: 'rgba(255,255,255,0.04)',
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
            }}
          >
            <XCircle size={22} />
          </button>
        </div>

        <div style={{ flex: 1, padding: 16, overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 10 }}>
          {messages.length === 0 ? (
            <div style={{ padding: 14, borderRadius: 14, border: '1px dashed rgba(255,255,255,0.10)', color: 'rgba(255,255,255,0.45)', fontWeight: 700 }}>
              Nenhuma mensagem ainda com Prof. {profName}. Envie sua dúvida acadêmica.
            </div>
          ) : (
            messages.map((m) => {
              const mine = m.fromId === fromId;
              return (
                <div key={m.id} style={{ display: 'flex', justifyContent: mine ? 'flex-end' : 'flex-start' }}>
                  <div
                    style={{
                      maxWidth: '75%',
                      padding: '10px 12px',
                      borderRadius: 16,
                      background: mine ? 'rgba(139,92,246,0.90)' : 'rgba(255,255,255,0.06)',
                      border: mine ? '1px solid rgba(139,92,246,0.25)' : '1px solid rgba(255,255,255,0.10)',
                      color: '#fff',
                      fontWeight: 600,
                      fontSize: 14,
                      lineHeight: 1.35,
                      whiteSpace: 'pre-wrap',
                    }}
                  >
                    {m.text}
                  </div>
                </div>
              );
            })
          )}
        </div>

        <div style={{ padding: 14, borderTop: '1px solid rgba(255,255,255,0.06)', display: 'flex', gap: 10 }}>
          <input
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
            placeholder="Digite sua mensagem..."
            style={{
              flex: 1,
              height: 44,
              borderRadius: 14,
              border: '1px solid rgba(255,255,255,0.12)',
              background: 'rgba(255,255,255,0.03)',
              color: '#fff',
              padding: '0 14px',
              outline: 'none',
              fontWeight: 600,
            }}
          />
          <button
            onClick={handleSend}
            style={{
              height: 44,
              padding: '0 16px',
              borderRadius: 14,
              border: '0',
              background: 'rgba(139,92,246,0.95)',
              color: '#fff',
              fontWeight: 900,
              cursor: 'pointer',
            }}
          >
            Enviar
          </button>
        </div>
      </motion.div>
    </div>,
    document.body
  );
}
